<template>
  <button
    :id="`c-button-${_uid}`"
    class="c-button"
    :type="type || 'button'"
    :class="{
      'c-button_responsive': responsive !== undefined,
      'c-button_nolabel': label === undefined,
      'c-button_with-icon': icon !== undefined,
      'c-button_active': activeLocal,
      'c-button_color_blue': color === 'blue',
      'c-button_color_pink': color === 'pink',
      'c-button_noborder': noBorder !== undefined,
      'c-button_size_small': size === 'small',
      'c-button_size_large': size === 'large',
      'c-button_active-color_blue': activeColor === 'blue',
      'c-button_disabled': disabled,
      'c-button_pending': pending,
    }"
  >
    <div class="c-button__wrapper" @click="click">
      <div v-if="icon !== undefined" class="c-button__icon">
        <c-icon :icon="typeof icon === 'string' ? icon : icon.icon" :color="getIconColor()" />
      </div>

      <div v-if="label !== undefined" class="c-button__label">
        {{ getLabel() }}
      </div>

      <div v-if="pending" class="c-button__pending">
        <font-awesome-icon
          :icon="spinner"
          spin
          :style="{
            color: getIconColor(),
          }"
        />
      </div>
    </div>

    <b-popover v-if="tooltip !== undefined" triggers="hover" placement="top" :target="`c-button-${_uid}`">
      {{ getPopoverContent() }}
    </b-popover>
  </button>
</template>

<script>
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import Icon from "@/components/icon";

export default {
  name: "c-button",

  components: {
    FontAwesomeIcon,
    "c-icon": Icon,
  },

  props: [
    "type",
    "icon",
    "label",
    "responsive",
    "tooltip",
    "activeColor",
    "active",
    "color",
    "noBorder",
    "size",
    "disabled",
    "pending",
  ],

  data() {
    return {
      spinner: faSpinner,
      activeLocal: this.active !== undefined ? this.active : false,
    };
  },

  watch: {
    active(value) {
      this.activeLocal = value;
    },
  },

  mounted() {
    // console.log('this: ', this._uid)
  },

  methods: {
    getLabel() {
      if (this.label.activeText) {
        return this.activeLocal ? this.label.activeText : this.label.text;
      }
      return this.label.text ? this.label.text : this.label;
    },

    click() {
      this.$emit("click");
    },

    getPopoverContent() {
      if (this.active !== undefined) {
        if (this.active) {
          return this.tooltip.activeText;
        }
        return this.tooltip.text;
      }
      return typeof this.tooltip === "string" ? this.tooltip : this.tooltip.text;
    },

    getIconColor() {
      if (this.icon.activeColor) {
        return this.activeLocal ? this.icon.activeColor : this.icon.color;
      }
      return this.icon.color;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";

.c-button_size_small.c-button_nolabel {
  .c-button__wrapper {
    width: 20px !important;
    height: 20px !important;
  }
}

.c-button_noborder {
  .c-button__wrapper {
    border: none;
  }
}

.c-button_size_large {
  .c-button__wrapper {
    padding: 7px 26px;
    border-radius: 19px;
  }

  .c-button__label {
    font-size: 15px;
    font-weight: 500;
  }
}

.c-button_disabled {
  opacity: 0.4;
  pointer-events: none;
}

.c-button {
  display: inline-block;
  line-height: inherit;
  font: inherit;
  border: none;
  background: transparent;
  padding: 0;

  &__wrapper {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    transition: 0.2s;
    border: 1px solid $color-button-border;
    position: relative;

    &:hover {
      border-color: $color-button-border-hover;
    }

    &:active {
      border-color: $color-button-border-clicked;
    }
  }

  &__pending {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba(255, 255, 255, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &_pending {
    .c-button__icon,
    .c-button__label {
      opacity: 0;
    }
  }

  &_active {
    &.c-button_active-color {
      &_blue {
        .c-button__wrapper {
          border-color: $color-blue;
          background: $color-blue;
        }
      }
    }
  }

  &_color {
    &_blue {
      .c-button__wrapper {
        border-color: $color-blue;
        background: $color-blue;
        color: white;
      }
    }

    &_pink {
      .c-button__wrapper {
        border-color: $color-pink;
        background: $color-pink;
        color: white;
      }
    }
  }

  &_responsive {
    @media screen and (max-width: 767.8px) {
      width: 30px;
      height: 30px;

      .c-button__wrapper {
        width: 100%;
        height: 100%;
        padding: 0 !important;
        display: flex;
        align-items: center;
        justify-content: center;

        & .c-button__icon {
          margin-left: 0 !important;
        }
      }

      .c-button__label {
        display: none;
      }

      .c-button__icon {
        margin-left: -10px !important;
        display: inline-block;
      }
    }
  }

  &_with-icon {
    &.c-button_nolabel {
      .c-button__wrapper {
        width: 30px;
        height: 30px;
        padding: 0;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .c-button__icon {
        width: 20px;
        height: 20px;
        margin: 0;
      }
    }

    .c-button {
      &__wrapper {
        height: 30px;
        box-sizing: border-box;
        padding: 0 15px;
        border-radius: 15px;
      }

      &__icon {
        margin-left: -5px;
        width: 20px;
        height: 20px;
      }

      &__label {
        margin-left: 8px;

        font: {
          size: 12px;
          weight: 500;
        }
      }
    }
  }
}
</style>
